import axios from "axios"
import memoizedRefreshToken from "./refreshToken"
import { ToolkitStore } from "@reduxjs/toolkit/dist/configureStore"
import { transformObjectKeysToCamelCase } from "./converter"
import { setAxiosToken } from "./requester"
import { isAuthError } from "utils"
import dayjs from "dayjs"

const headers: any = {
  "X-User-Agent": import.meta.env.VITE_ZOZO_FIT_API_USER_AGENT,
  "X-Timezone-Offset": dayjs().format("Z").split(":")[0],
}

if (import.meta.env.VITE_ZOZO_FIT_BASIC_AUTH_TOKEN) {
  headers["Authorization"] = `Basic ${
    import.meta.env.VITE_ZOZO_FIT_BASIC_AUTH_TOKEN
  }`
}

const zozoFitInstance = axios.create({
  baseURL: import.meta.env.VITE_ZOZO_FIT_API_BASE_URL,
  headers,
})

export function setZOZOFitAxiosToken(token: string) {
  zozoFitInstance.defaults.headers.common[
    "X-Authorization-ACCESSTOKEN"
  ] = `Bearer ${token}`
}
export function removeZOZOFitAxiosToken() {
  delete zozoFitInstance.defaults.headers.common["X-Authorization-ACCESSTOKEN"]
}

const responseBody = (res: any) => transformObjectKeysToCamelCase(res.data)

const zozoFitRequester = {
  get: (url: string, params?: any, config = {}) =>
    zozoFitInstance
      .get(url, {
        params,
        ...config,
      })
      .then(responseBody),

  post: (url: string, data: any, config = {}) =>
    zozoFitInstance.post(url, data, config).then(responseBody),
  put: (url: string, data: any, config = {}) =>
    zozoFitInstance.put(url, data, config).then(responseBody),
  delete: (url: string, data?: any) =>
    zozoFitInstance.delete(url, { data }).then(responseBody),
  patch: (url: string, data: any) =>
    zozoFitInstance.patch(url, data).then(responseBody),
}

export const setupZozoFitInstanceInterceptor = (store: ToolkitStore) => {
  zozoFitInstance.interceptors.response.use(
    (response) => {
      if (response.data) {
        response.data = transformObjectKeysToCamelCase(response.data)
      }

      return response
    },
    async (error: any) => {
      const config = error?.config

      const isAuthEndpoints = config?.url?.includes("/accounts/token")
      const refreshToken = store.getState().auth.refreshToken

      if (
        isAuthError(error) &&
        !config?.sent &&
        !isAuthEndpoints &&
        refreshToken
      ) {
        config.sent = true

        const result = await memoizedRefreshToken(store)

        if (result?.accessToken) {
          config.headers = {
            ...config.headers,
            ["X-Authorization-ACCESSTOKEN"]: `Bearer ${result.accessToken}`,
          }
          setZOZOFitAxiosToken(result.accessToken)
          setAxiosToken(result.accessToken)
          return zozoFitInstance(config)
        }

        return new Promise(() => {})
      }

      return Promise.reject(error)
    },
  )
}

export default zozoFitRequester
